import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/base/LanguageSwitcher';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.services', path: '/servicios' },
    { labelKey: 'nav.products', path: '/productos' },
    { labelKey: 'nav.about', path: '/nosotros' },
    { labelKey: 'nav.contact', path: '/contacto' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen ? 'bg-[#0A1628] shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="bg-white rounded-xl px-3 py-1.5">
            <img
              src="https://static.readdy.ai/image/dfc6db26b98ba2c0b04cea29bf715fac/ef60e386036071c16748ee6a2c11d513.png"
              alt="Printven LLC Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-[#FF6B35]'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {t(link.labelKey)}
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <a
            href="https://wa.me/17542034748"
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#1ebe5a] transition-colors"
          >
            <i className="ri-whatsapp-line text-base"></i>
            {t('nav.whatsapp')}
          </a>
          <Link
            to="/servicios"
            className="bg-[#FF6B35] text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#e85a24] transition-colors"
          >
            {t('nav.quote')}
          </Link>
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menú"
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A1628] border-t border-white/10 px-4 pb-6">
          <ul className="flex flex-col gap-1 pt-4">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`block py-3 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive(link.path)
                      ? 'text-[#FF6B35] bg-white/5'
                      : 'text-white/80 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-3 mt-4">
            <div className="flex justify-center">
              <LanguageSwitcher />
            </div>
            <a
              href="https://wa.me/17542034748"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-semibold py-3 rounded-full whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base"></i>
              {t('nav.whatsapp_full')}
            </a>
            <Link
              to="/servicios"
              className="flex items-center justify-center bg-[#FF6B35] text-white text-sm font-semibold py-3 rounded-full whitespace-nowrap cursor-pointer"
            >
              {t('nav.quote')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
